import { groupColor } from '../../engine';

export interface Padel24GroupBoardProps {
  /** The Padel24 tournament object (events[i].obj). */
  tournament: any;
  /** Unified id → full name resolver from the dashboard. */
  nameOf: (id: any) => string;
}

// ── Padel24 group board ──
// A compact, systematic view of the CURRENT phase: one card per fasegruppe with
// its live mini-standings (sorted by wins → games-diff → games) plus that group's
// matches laid out in rows. Mirrors the tournament «Grupper»-tab ranking logic but
// stripped down for the big-screen dashboard. Phase 2+ keeps each match inside a
// single group (m.primaryGroup); phase 1 buckets pair-rounds by their primaryGroup.
export function Padel24GroupBoard({ tournament: t, nameOf }: Padel24GroupBoardProps) {
  const phase = t.phase || 1;
  const groups = t.groups || [];
  const standings = t.standings || {};
  const promo = t.config?.promotionCount || 3;
  const isDone = t.status === 'done' || t.status === 'finals';

  const phaseMatches = (t.matches || []).filter((m: any) => m.phase === phase);
  const matchGroupOf = (m: any) => m.primaryGroup || m.groupContext || null;

  const first = (id: any) => (nameOf(id) || 'Spiller').split(' ')[0];
  const slotName = (s: any) => (!s ? null : s.kind === 'concrete' ? first(s.playerId) : null);
  const teamLabel = (team: any) => {
    const names = [slotName(team?.p1), slotName(team?.p2)].filter(Boolean);
    return names.length ? names.join('/') : '?';
  };

  const sortGroup = (players: any[]) =>
    [...players].sort((a, b) => {
      const A = standings[a.id] || {};
      const B = standings[b.id] || {};
      if ((B.wins || 0) !== (A.wins || 0)) return (B.wins || 0) - (A.wins || 0);
      const dA = (A.gamesWon || 0) - (A.gamesLost || 0);
      const dB = (B.gamesWon || 0) - (B.gamesLost || 0);
      if (dA !== dB) return dB - dA;
      return (B.gamesWon || 0) - (A.gamesWon || 0);
    });

  if (!groups.length)
    return <div className="text-center py-6 text-ink-3 text-sm">Ingen grupper.</div>;

  return (
    <div className="space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        {isDone ? 'Sluttoppstilling' : `Fase ${phase} av ${t.phasesTotal || phase}`} ·{' '}
        {groups.length} grupper
      </div>
      <div
        className="grid gap-2.5"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
      >
        {groups.map((g: any, gi: number) => {
          const colors = groupColor(g.letter);
          const sorted = sortGroup(g.players || []);
          const gMatches = phaseMatches
            .filter((m: any) => matchGroupOf(m) === g.letter)
            .sort(
              (a: any, b: any) =>
                (a.round || 0) - (b.round || 0) || (a.court || 0) - (b.court || 0),
            );
          const showPromo = !isDone;
          return (
            <div
              key={g.letter}
              className="rounded-xl overflow-hidden"
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderLeft: `4px solid ${colors.accent}`,
              }}
            >
              {/* Group header */}
              <div
                className="flex items-center gap-2 px-3 py-2"
                style={{ background: colors.bg + '55' }}
              >
                <span
                  className="font-display text-xs font-bold px-1.5 py-0.5 rounded"
                  style={{ background: colors.bg, color: colors.text }}
                >
                  {g.letter}
                  {phase}
                </span>
                <span className="text-[12px] font-semibold text-ink-1">Gruppe {g.letter}</span>
                <span className="ml-auto font-mono text-[10px] text-ink-3">
                  {(g.players || []).length} spillere
                </span>
              </div>

              {/* Mini standings */}
              <div
                className="grid items-center gap-1 px-3 py-1 text-[9px] font-mono font-semibold uppercase tracking-wider text-ink-3"
                style={{ gridTemplateColumns: '16px 1fr 22px 28px 26px' }}
              >
                <div></div>
                <div>Spiller</div>
                <div className="text-center" title="Seire">
                  V
                </div>
                <div className="text-center" title="Games-differanse">
                  +/−
                </div>
                <div className="text-center" title="Games vunnet">
                  G
                </div>
              </div>
              {sorted.map((p: any, i: number) => {
                const s = standings[p.id] || {};
                const diff = (s.gamesWon || 0) - (s.gamesLost || 0);
                const isUp = showPromo && gi > 0 && i < promo;
                const isDown = showPromo && gi < groups.length - 1 && i >= sorted.length - promo;
                return (
                  <div
                    key={p.id}
                    className="grid items-center gap-1 px-3 py-[3px] text-[12px]"
                    style={{
                      gridTemplateColumns: '16px 1fr 22px 28px 26px',
                      background: isUp
                        ? 'rgba(132,204,22,0.12)'
                        : isDown
                          ? 'rgba(249,115,22,0.10)'
                          : undefined,
                    }}
                  >
                    <span className="font-mono text-[10px] text-ink-3">{i + 1}</span>
                    <span className="truncate text-ink-1">{nameOf(p.id) || 'Spiller'}</span>
                    <span className="text-center font-mono font-semibold text-ink-1">
                      {s.wins || 0}
                    </span>
                    <span
                      className="text-center font-mono font-semibold text-ink-3"
                      style={diff !== 0 ? { color: diff > 0 ? '#3f6212' : '#be123c' } : undefined}
                    >
                      {diff > 0 ? '+' : ''}
                      {diff}
                    </span>
                    <span className="text-center font-mono text-ink-2">{s.gamesWon || 0}</span>
                  </div>
                );
              })}

              {/* Group matches — systematic rows (round · court · pairing — score — pairing) */}
              {gMatches.length > 0 && (
                <div className="border-t border-line-soft mt-1 pt-1.5 pb-1.5 px-3 space-y-1">
                  <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
                    Kamper
                  </div>
                  {gMatches.map((m: any, mi: number) => {
                    const live = m.liveScore?.score1 != null;
                    const score = live
                      ? `${m.liveScore.score1}-${m.liveScore.score2 ?? 0}`
                      : m.completed
                        ? `${m.score1}-${m.score2}`
                        : '–';
                    return (
                      <div key={mi} className="flex items-center gap-1.5 text-[11px]">
                        <span className="font-mono text-[9px] text-ink-3 w-10 flex-shrink-0">
                          B{m.court}
                          {m.time ? ` ${m.time}` : ''}
                        </span>
                        <span className="flex-1 text-right truncate text-ink-2">
                          {teamLabel(m.team1)}
                        </span>
                        <span
                          className={`font-mono tabular-nums flex-shrink-0 px-1 ${live ? 'font-bold' : m.completed ? 'font-semibold' : 'text-ink-3'}`}
                          style={
                            live
                              ? { color: '#3f6212' }
                              : m.completed
                                ? { color: '#0e7490' }
                                : undefined
                          }
                        >
                          {score}
                        </span>
                        <span className="flex-1 truncate text-ink-2">{teamLabel(m.team2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
